// theme/index.js (or theme.js)
import { createContext, useMemo, useState } from "react";
import { createTheme } from "@mui/material/styles";

/** --------- Your existing timereq generator (kept) --------- */
const generateGreenShades = () => {
  const shades = [];
  for (let i = 1; i <= 100; i++) {
    let modIndex = (i - 2) % 5;
    let saturation = 100;
    let lightness = 40 - modIndex * 7;
    if (lightness < 5) lightness = 5;
    shades.push(`hsl(120, ${saturation}%, ${lightness}%)`);
  }
  return shades;
};

/** --------- Design tokens (fixed greys + accents) --------- */
export const tokens = (mode) => {
  // Correct grey ramps: 100 = lightest, 900 = darkest (light mode)
  const lightGreys = {
    50:  "#fafafa",
    100: "#f5f5f5",
    200: "#eeeeee",
    300: "#e0e0e0",
    400: "#bdbdbd",
    500: "#9e9e9e",
    600: "#757575",
    700: "#616161",
    800: "#424242",
    900: "#212121",
  };

  // Dark mode greys (mirrored to stay visually consistent)
  const darkGreys = {
    50:  "#121212",
    100: "#1e1e1e",
    200: "#232323",
    300: "#2c2c2c",
    400: "#333333",
    500: "#4d4d4d",
    600: "#6b6b6b",
    700: "#8c8c8c",
    800: "#b3b3b3",
    900: "#e0e0e0",
  };

  const greys = mode === "light" ? lightGreys : darkGreys;

  return {
    grey: greys,
    // Keep primary neutral (not green), we’ll set primary.main separately in themeSettings
    primary: {
      100: greys[100],
      200: greys[200],
      300: greys[300],
      400: greys[400],
      500: greys[500],
      600: greys[600],
      700: greys[700],
      800: greys[800],
      900: greys[900],
    },

    timereq: generateGreenShades(),

    // Your green brand accents (kept, normalized from lightest→darkest)
    greenAccent: {
      100: "#dbf5ee",
      200: "#b7ebde",
      300: "#94e2cd",
      400: "#70d8bd",
      500: "#4cceac",
      600: "#3da58a",
      700: "#2e7c67",
      800: "#1e5245",
      900: "#0f2922",
    },
    redAccent: {
      100: "#f8dcdb",
      200: "#f1b9b7",
      300: "#e99592",
      400: "#e2726e",
      500: "#db4f4a",
      600: "#af3f3b",
      700: "#832f2c",
      800: "#58201e",
      900: "#2c100f",
    },
    blueAccent: {
      100: "#e1e2fe",
      200: "#c3c6fd",
      300: "#a4a9fc",
      400: "#868dfb",
      500: "#6870fa",
      600: "#535ac8",
      700: "#3e4396",
      800: "#2a2d64",
      900: "#151632",
    },
  };
};

/** --------- MUI theme settings (default light) --------- */
export const themeSettings = (mode) => {
  const colors = tokens(mode);

  return {
    palette: {
      mode,

      primary: {
        main: "#62BDA4",
        light: "#4FD7BA",
        dark: "#14997E",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#1ACCA9",
      },
     

      background: {
        default: '#ffffff', // Default Background color for all pages
        paper: "#ffffff",
        box: colors.grey[200]
      },
      text: {
        primary: mode === "light" ? colors.grey[900] : colors.grey[900],
        secondary: mode === "light" ? colors.grey[700] : colors.grey[800],
      },
      divider: mode === "light" ? colors.grey[300] : colors.grey[400],
    },

    shape: { borderRadius: 10 },

    components: {
      MuiDataGrid: {
        styleOverrides: {
          root: ({ theme }) => ({
            "--DataGrid-rowBorderColor": theme.palette.divider,
            "& .MuiDataGrid-cell": {
              borderBottomColor: theme.palette.divider,
            },
            "& .MuiDataGrid-columnHeaders": {
              borderBottomColor: theme.palette.divider,
            },
            "& .MuiDataGrid-footerContainer": {
              borderTopColor: theme.palette.divider,
            },
          }),
        },
      },
      
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: "none",
            fontWeight: 600,
          }),
          outlined: ({ theme }) => ({
            color: theme.palette.text.primary,
            borderColor: theme.palette.divider,
          }),
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.secondary,
          }),
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.divider,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.text.secondary,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.primary.main,
            },
            "& input": {
              color: theme.palette.text.primary,
            },
          }),
        },
      },

      MuiFilledInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            "&:before": { borderColor: theme.palette.divider },
            "&:hover:not(.Mui-disabled):before": {
              borderColor: theme.palette.text.secondary,
            },
          }),
        },
      },
    },

    typography: {
      fontFamily: ["Poppins", "Roboto", "Source Sans Pro", "sans-serif"].join(","),
      fontSize: 12,
      h1: { fontSize: 40, fontWeight: 700 },
      h2: { fontSize: 32, fontWeight: 700 },
      h3: { fontSize: 24, fontWeight: 600 },
      h4: { fontSize: 20, fontWeight: 600 },
      h5: { fontSize: 16, fontWeight: 600 },
      h6: { fontSize: 12, fontWeight: 400 },
    },
  };
};

/** --------- Color mode context (fixed name) --------- */
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});



/** --------- Hook to use the theme (default to LIGHT) --------- */
export const useMode = () => {
  const [mode, setMode] = useState("light");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => setMode((prev) => (prev === "light" ? "dark" : "light")),
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  return [theme, colorMode];
};
